<template>
  <div class="rule-form-step rule-form-step--ai-mode rule-form-step--ai-mode--step-1 flex max-w-4xl flex-col gap-5 py-5">
    <form-block
      :title="tr('Your goal')"
      :description="tr('Depending on what you want to achieve with your ad, different settings will be the most optimal for running ads. By defining the goal, :app will make these settings automatically.')"
      class="goal-block"
    >
      <ui-radio-group v-model="goal" class="grid gap-2.5 sm:grid-cols-2">
        <label
          v-for="option in GOAL_OPTIONS"
          :key="option.value"
          :class="[
            'glass-tile flex cursor-pointer items-start gap-3 rounded-xl p-3 text-sm',
            GLOW_TRANSITION,
            goal === option.value ? 'ring-2 ring-primary' : 'hover:[--tile-glow:color-mix(in_oklab,var(--primary)_40%,transparent)]',
          ]"
        >
          <ui-radio-group-item :value="option.value" class="mt-0.5" />
          <i :class="[ 'icon primary size-8 shrink-0', option.icon ]" />
          <span class="flex min-w-0 flex-col gap-0.5">
            <span class="font-semibold text-foreground">{{ tr(option.title) }}</span>
            <span class="text-xs text-muted-foreground">{{ tr(option.description) }}</span>
          </span>
        </label>
      </ui-radio-group>
    </form-block>

    <brand-block
      ref="brandBlock"
      :entity="entity"
    />

    <conversion-odax-block
      v-if="conversionEventSettingsAvailable"
      ref="conversionOdaxBlock"
      :entity="entity"
      :disabled="$parent.readonlyMode"
      minimal
    />

    <source-block ref="sourceBlock" :entity="entity" :disabled="$parent.readonlyMode" />

    <leadgen-form-block
      v-if="goal === 'leads'"
      ref="leadgenFormBlock"
      :entity="entity"
      :disabled="$parent.readonlyMode"
    />

    <ad-content-sources-block
      v-if="entity.isSalesCampaign"
      :entity="entity"
      :disabled="$parent.readonlyMode"
      hide-when-no-catalogues
    />

    <form-block v-if="ctaUrlIsAvailable" ref="ctaUrlBlock">
      <div class="flex flex-col gap-1.5">
        <ui-label for="ai-mode-cta-url">{{ ctaUrlLabel }}</ui-label>
        <ui-input
          id="ai-mode-cta-url"
          v-model="entity.settings.cta_url"
          :placeholder="tr('Enter the URL you want to promote')"
          @blur="() => onWebsiteInputBlur()"
        />
        <p class="m-0 text-xs text-muted-foreground">
          {{ tr('If the post contains at least one link, we will use it as the Button URL to create the ad. In this case, the URL provided here will only be used if the post does not contain any links.') }}
        </p>
      </div>
    </form-block>

    <activity-management-block
      :entity="entity"
      :disabled="$parent.readonlyMode"
      :disable-block-in-ai-mode="true"
    />

    <form-block v-if="goal !== 'engagement'" :title="tr('AI Assist')">
      <label :class="[ 'flex w-fit items-center gap-2 text-sm text-foreground', $parent.readonlyMode ? 'cursor-not-allowed' : 'cursor-pointer' ]">
        <ui-checkbox
          :model-value="aiAssist.includes(AI_ASSIST_OPTIONS.CREATE_CREATIVES)"
          :disabled="$parent.readonlyMode"
          @update:model-value="(checked) => toggleAiAssist(AI_ASSIST_OPTIONS.CREATE_CREATIVES, checked)"
        />
        {{ tr('Create Ai creatives') }}
        <info-popup>
          <div v-html="tr('When :app creates ad variations, it generates new ads instead of boosting your original post. This means engagement will appear on the ads, not on your original post.')" />
        </info-popup>
      </label>
    </form-block>

    <campaign-budget-block
      ref="campaignBudgetBlock"
      :entity="entity"
      :title="tr('Daily budget')"
      :description="tr('You can determine the maximum daily budget that you allocate for advertising.')"
      :switchable="false"
      minimal
    />

    <dsa-block ref="dsaBlock" :entity="entity" :disabled="$parent.readonlyMode" />

    <div class="flex flex-col gap-5">
      <demographic-target-block
        :title="tr('Target audience')"
        :entity="entity"
        :disabled="$parent.readonlyMode"
        :age-available="audienceCompatibilityValidation.age.available"
        :age-message="audienceCompatibilityValidation.age.message"
        :age-message-type="audienceCompatibilityValidation.age.message_type"
        :gender-available="audienceCompatibilityValidation.gender.available"
        :gender-message="audienceCompatibilityValidation.gender.message"
        :gender-message-type="audienceCompatibilityValidation.gender.message_type"
      />

      <div ref="geotargetWrapper">
        <geotarget-block
          ref="geotargetBlock"
          :entity="entity"
          :disabled="$parent.readonlyMode"
          :title="tr('Geotargeting')"
          :geo-available="audienceCompatibilityValidation.geo.available"
          :geo-message="audienceCompatibilityValidation.geo.message"
          :geo-message-type="audienceCompatibilityValidation.geo.message_type"
          :language-available="audienceCompatibilityValidation.language.available"
          :language-message="audienceCompatibilityValidation.language.message"
          :language-message-type="audienceCompatibilityValidation.language.message_type"
        />
      </div>
    </div>

    <campaign-special-ad-categories-block :entity="entity" :disabled="!entity.isNew" />
  </div>
</template>

<script>
import AiModeGuidedTour from './AiModeGuidedTour.js';
import { AI_ASSIST_OPTIONS, GOAL_OPTIONS } from '@/consts';
import AudienceCompatibilityValidation from '@/descriptors/AudienceCompatibilityValidation.js';
import FacebookTargeting from '@/descriptors/FacebookTargeting';
import RuleFormStepMixin from '@/mixins/RuleForm/Step.js';
import AudienceService from '@/services/Audience';
import FormBlock from '@/ui/components/FormBlock.vue';
import InfoPopup from '@/ui/components/InfoPopup.vue';
import ActivityManagementBlock from '@/ui/components/RuleForm/Blocks/ActivityManagementBlock.vue';
import AdContentSourcesBlock from '@/ui/components/RuleForm/Blocks/AdContentSourcesBlock.vue';
import BrandBlock from '@/ui/components/RuleForm/Blocks/BrandBlock.vue';
import CampaignBudgetBlock from '@/ui/components/RuleForm/Blocks/CampaignBudgetBlock.vue';
import CampaignSpecialAdCategoriesBlock from '@/ui/components/RuleForm/Blocks/CampaignSpecialAdCategoriesBlock.vue';
import ConversionOdaxBlock from '@/ui/components/RuleForm/Blocks/ConversionOdaxBlock.vue';
import DemographicTargetBlock from '@/ui/components/RuleForm/Blocks/DemographicTargetBlock.vue';
import DsaBlock from '@/ui/components/RuleForm/Blocks/DsaBlock.vue';
import GeotargetBlock from '@/ui/components/RuleForm/Blocks/GeotargetBlock.vue';
import LeadgenFormBlock from '@/ui/components/RuleForm/Blocks/LeadgenFormBlock.vue';
import SourceBlock from '@/ui/components/RuleForm/Blocks/SourceBlock.vue';
import { GLOW_TRANSITION } from '@/ui/utils/ruleAction.js';
import debounce from '@/utils/debounce.js';
import { formatNumber, formatPrice } from '@/utils/number.js';
import { isValidURL } from '@/utils/validators';

export default {
  name: 'AiModeRuleFormStep1',
  components: {
    ActivityManagementBlock,
    BrandBlock,
    AdContentSourcesBlock,
    CampaignBudgetBlock,
    CampaignSpecialAdCategoriesBlock,
    ConversionOdaxBlock,
    DemographicTargetBlock,
    DsaBlock,
    GeotargetBlock,
    LeadgenFormBlock,
    SourceBlock,
    FormBlock,
    InfoPopup,
  },
  mixins: [ RuleFormStepMixin ],
  data() {
    return {
      AI_ASSIST_OPTIONS,
      GLOW_TRANSITION,
      GOAL_OPTIONS,
      shouldShowScalingBudgetError: false,
      debouncedCheckScalingBudget: null,
      guidedTour: null,
      audienceCompatibilityValidation: new AudienceCompatibilityValidation(),
      debouncedValidateAudienceCompatibility: debounce(() => this.validateAudienceCompatibility(), 25),
    };
  },
  computed: {
    goal: {
      get() {
        return this.$store.getters['ruleForm/goal'];
      },
      set(value) {
        this.$store.commit('ruleForm/setGoal', value);
      },
    },
    aiAssist: {
      get() {
        return this.$store.getters['ruleForm/aiAssist'];
      },
      set(value) {
        this.$store.commit('ruleForm/setAiAssist', value);
      },
    },
    budget: {
      get() {
        return this.$store.getters['ruleForm/budget'];
      },
      set(value) {
        this.$store.commit('ruleForm/setBudget', value);
      },
    },
    scalingBudget: {
      get() {
        return this.$store.getters['ruleForm/scalingBudget'];
      },
      set(value) {
        this.$store.commit('ruleForm/setScalingBudget', value);
      },
    },
    conversionEventSettingsAvailable() {
      return this.goal === 'conversion' || this.goal === 'app_promotion';
    },
    scalingBudgetIsAvailable() {
      return this.goal === 'conversion' || this.goal === 'traffic';
    },
    invalidScalingBudget() {
      return this.scalingBudget && this.scalingBudget < this.entity.adAccountMinDailyBugdet;
    },
    invalidScalingBudgetInfoText() {
      return this.tr('Budget must be more than <span class="budget">:value</span>!', { ':value' : formatPrice(this.entity.adAccountMinDailyBugdet, this.entity.adAccountCurrency.symbol) });
    },
    availableScaledAdsFromBudget() {
      if (this.scalingBudget >= this.entity.adAccountMinDailyBugdet) {
        return Math.floor(this.scalingBudget / this.entity.adAccountMinDailyBugdet);
      }

      return null;
    },
    ctaUrlIsAvailable() {
      return this.ctaUrlLabel !== null && this.entity.ad_account.id !== null;
    },
    ctaUrlLabel() {
      switch (this.goal) {
        case 'conversion':
          return this.tr('Product or service sales link');

        case 'traffic':
          return this.tr('Website link');

        default:
          return null;
      }
    },
    selectedBrand() {
      if (!this.entity.brand || !this.entity.brand.id) {
        return null;
      }
      const brands = this.$root.currentCompany.brands || [];
      return brands.find(b => b.id === this.entity.brand.id) || null;
    },
  },
  watch: {
    'entity.settings.adset_targeting.age_min': {
      handler: 'debouncedValidateAudienceCompatibility',
    },
    'entity.settings.adset_targeting.geo_locations': {
      handler: 'debouncedValidateAudienceCompatibility',
      deep: true,
    },
    'entity.output_campaign.special_ad_categories': {
      handler: 'debouncedValidateAudienceCompatibility',
      deep: true,
    },
    'entity.output_campaign.special_ad_category_country': {
      handler: 'debouncedValidateAudienceCompatibility',
      deep: true,
    },
    goal: {
      immediate: true,
      handler(val) {
        this.entity.settings.cta_url = null;

        switch (val) {
          case 'conversion':
            this.entity.output_campaign.objective = 'OUTCOME_SALES';
            break;

          case 'traffic':
            this.entity.output_campaign.objective = 'OUTCOME_TRAFFIC';
            break;

          case 'app_promotion':
            this.entity.output_campaign.objective = 'OUTCOME_APP_PROMOTION';
            break;

          case 'engagement':
          case 'page_like':
            this.entity.output_campaign.objective = 'OUTCOME_ENGAGEMENT';
            break;

          case 'leads':
            this.entity.output_campaign.objective = 'OUTCOME_LEADS';
            break;
        }

        if (val !== 'leads') {
          this.entity.settings.leadgen_form_id = null;
        }

        if (val === 'engagement') {
          this.$store.commit('ruleForm/setAiAssist', []);
        }
        else {
          this.$store.commit('ruleForm/setAiAssist', [ AI_ASSIST_OPTIONS.CREATE_CREATIVES ]);
        }
      },
    },
    conversionEventSettingsAvailable(val) {
      if (!val) {
        this.entity.settings.adset_conversion_app_id = null;
        this.entity.settings.adset_conversion_app_store_url = null;
        this.entity.settings.adset_conversion_pixel_id = null;
        this.entity.settings.adset_conversion_event = null;
      }
    },
    scalingBudgetIsAvailable(val) {
      if (!val) {
        this.scalingBudget = null;
      }
    },
    scalingBudget(val) {
      if (val !== null && this.entity.adAccountMinDailyBugdet !== null) {
        if (val < this.entity.adAccountMinDailyBugdet) {
          this.debouncedCheckScalingBudget();
        }
        else {
          this.shouldShowScalingBudgetError = false;
        }
      }
    },
    selectedBrand(brand) {
      if (!brand) {
        return;
      }

      if (brand.targeting) {
        this.entity.settings.adset_targeting = new FacebookTargeting(brand.targeting);
      }

      if (this.ctaUrlIsAvailable && brand.website) {
        this.entity.settings.cta_url = brand.website;
      }
    },
    ctaUrlIsAvailable(val) {
      if (!val) {
        this.entity.settings.cta_url = null;
      }
      else if (this.selectedBrand && this.selectedBrand.website) {
        this.entity.settings.cta_url = this.selectedBrand.website;
      }
    },
  },
  created() {
    if (!this.entity.dsa_beneficiary) {
      this.entity.dsa_beneficiary = this.$root.currentCompany.billing_company || this.$root.currentCompany.name;
    }
    this.debouncedCheckScalingBudget = debounce(() => {
      this.shouldShowScalingBudgetError = this.scalingBudget && this.scalingBudget < this.entity.adAccountMinDailyBugdet;
    }, 800);
  },
  mounted() {
    if (this.$route.query.guided !== undefined) {
      this.guidedTour = new AiModeGuidedTour(this);
      setTimeout(() => this.guidedTour.start(), 200);
    }
  },
  beforeUnmount() {
    if (this.guidedTour) {
      this.guidedTour.end();
    }
  },
  methods: {
    toggleAiAssist(option, checked) {
      this.aiAssist = checked
        ? [ ...this.aiAssist, option ]
        : this.aiAssist.filter(item => item !== option);
    },
    formatNumber,
    validateAudienceCompatibility() {
      if (!this.entity.ad_account.id) {
        return;
      }

      const adAccountId = this.entity.ad_account.id;
      const adsetTargeting = this.entity.settings.adset_targeting || {};
      const minAge = adsetTargeting.age_min;
      const geoLocations = adsetTargeting.geo_locations?.toObject() || [];
      const specialAdCategories = this.entity.output_campaign.special_ad_categories || [];
      const specialAdCategoryCountries = this.entity.output_campaign.special_ad_category_country || [];

      AudienceService.validateAudienceCompatibility(
        adAccountId,
        minAge,
        false,
        [],
        geoLocations,
        specialAdCategories,
        specialAdCategoryCountries,
      ).then(validation => {
        this.audienceCompatibilityValidation = validation;
        return validation;
      }).catch(() => {
        this.audienceCompatibilityValidation = new AudienceCompatibilityValidation();
      });
    },
    validate() {
      if (!this.goal) {
        this.error(this.tr('Defining the goal is mandatory.'));
        return false;
      }

      if (!this.validateBlocks()) {
        return false;
      }

      if (this.ctaUrlIsAvailable && !isValidURL(this.entity.settings.cta_url)) {
        this.error(this.tr('Invalid website URL.'));
        return false;
      }

      return true;
    },
    onWebsiteInputBlur() {
      if (
        this.entity.settings.cta_url
        && isValidURL(this.entity.settings.cta_url)
        && this.entity.settings.cta_url.toLowerCase().indexOf('http') !== 0
      ) {
        this.entity.settings.cta_url = 'https://' + this.entity.settings.cta_url;
      }
    },
    onScalingBudgetBlur() {
      this.debouncedCheckScalingBudget.cancel();
      this.shouldShowScalingBudgetError = this.scalingBudget && this.scalingBudget < this.entity.adAccountMinDailyBugdet;
    },
  },
};
</script>
